import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import FoundersRateBanner from "../components/FoundersRateBanner";
import IdentityVerificationCard from "../components/IdentityVerificationCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Eye, EyeOff, Briefcase, User, Wrench, Loader2, ArrowRight, ArrowLeft, CheckCircle2, XCircle, Shield, Crown, Fingerprint, CreditCard } from "lucide-react";

const COUNTRIES = [
  { code: "US", name: "United States", currency: "USD" },
  { code: "GB", name: "United Kingdom", currency: "GBP" },
  { code: "CA", name: "Canada", currency: "CAD" },
  { code: "AU", name: "Australia", currency: "AUD" },
  { code: "DE", name: "Germany", currency: "EUR" },
  { code: "FR", name: "France", currency: "EUR" },
  { code: "JP", name: "Japan", currency: "JPY" },
  { code: "IN", name: "India", currency: "INR" },
  { code: "MX", name: "Mexico", currency: "MXN" },
  { code: "BR", name: "Brazil", currency: "BRL" },
  { code: "NG", name: "Nigeria", currency: "NGN" },
];

const PAYMENT_METHODS = [
  { id: "cash", name: "Cash" },
  { id: "card", name: "Credit/Debit Card" },
  { id: "paypal", name: "PayPal" },
  { id: "venmo", name: "Venmo" },
  { id: "zelle", name: "Zelle" },
  { id: "cashapp", name: "Cash App" },
  { id: "applepay", name: "Apple Pay" },
  { id: "googlepay", name: "Google Pay" },
  { id: "check", name: "Check" },
  { id: "bank_transfer", name: "Bank Transfer" },
];

// Password strength validation
const validatePassword = (password) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
  const isValid = Object.values(checks).every(Boolean);
  return { checks, isValid };
};

export default function RegisterPage({ type: propType }) {
  const { registerCustomer, registerProfessional } = useAuth();
  const navigate = useNavigate();
  const [userType, setUserType] = useState(propType || null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [categories, setCategories] = useState([]);
  const [step, setStep] = useState(1);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    phone: "",
    country: "US",
    currency: "USD",
    // Professional specific
    business_name: "",
    bio: "",
    service_area: "",
    selectedCategories: [],
    accepted_payments: ["cash", "card"]
  });

  const [foundersEligibility, setFoundersEligibility] = useState(null);

  // Check founder's rate eligibility when service area changes
  useEffect(() => {
    const checkFoundersRate = async () => {
      if (userType === "professional" && formData.service_area.length > 2) {
        try {
          const response = await axios.get(`${API}/subscriptions/founders-rate?service_area=${encodeURIComponent(formData.service_area)}`);
          setFoundersEligibility(response.data);
        } catch (error) {
          console.error("Failed to check founders rate:", error);
        }
      }
    };
    
    const debounce = setTimeout(checkFoundersRate, 500);
    return () => clearTimeout(debounce);
  }, [formData.service_area, userType]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        toast.error("Failed to load categories");
      }
    };
    fetchCategories();
  }, []);

  const handleCountryChange = (countryCode) => {
    const country = COUNTRIES.find(c => c.code === countryCode);
    setFormData({
      ...formData,
      country: countryCode,
      currency: country?.currency || "USD"
    });
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      selectedCategories: prev.selectedCategories.includes(categoryId)
        ? prev.selectedCategories.filter(id => id !== categoryId)
        : [...prev.selectedCategories, categoryId]
    }));
  };

  const handlePaymentToggle = (paymentId) => {
    setFormData(prev => ({
      ...prev,
      accepted_payments: prev.accepted_payments.includes(paymentId)
        ? prev.accepted_payments.filter(id => id !== paymentId)
        : [...prev.accepted_payments, paymentId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (userType === "customer") {
        const user = await registerCustomer({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          phone: formData.phone,
          country: formData.country,
          currency: formData.currency
        });
        // Track Google Ads conversion
        if (window.trackConversion) {
          window.trackConversion('customer_signup', 0);
        }
        toast.success("Account created successfully!");
        navigate("/dashboard");
      } else {
        if (formData.selectedCategories.length === 0) {
          toast.error("Please select at least one service category");
          setLoading(false);
          return;
        }
        
        // NEW: Initiate registration with payment instead of direct registration
        const response = await axios.post(`${API}/auth/register/professional/initiate`, {
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          phone: formData.phone,
          country: formData.country,
          currency: formData.currency,
          business_name: formData.business_name,
          categories: formData.selectedCategories,
          bio: formData.bio,
          service_area: formData.service_area,
          accepted_payments: formData.accepted_payments
        });
        
        // Redirect to Stripe Checkout for $10 registration fee
        if (response.data.checkout_url) {
          // Track Google Ads conversion
          if (window.trackConversion) {
            window.trackConversion('professional_signup', 75);
          }
          toast.success("Redirecting to secure payment...");
          window.location.href = response.data.checkout_url;
        } else {
          toast.error("Payment setup failed. Please try again.");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // Post-Registration Verification Prompt (Professional Only)
  if (showVerificationPrompt) {
    return (
      <div className="min-h-screen bg-sand">
        <Header />
        
        <div className="flex items-center justify-center min-h-screen pt-20 px-4">
          <div className="w-full max-w-xl animate-fadeInUp">
            <Card className="border-0 shadow-xl" data-testid="verification-prompt">
              <CardContent className="p-8">
                {/* Success Header */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="font-heading font-bold text-2xl text-gray-900 mb-2">
                    Welcome to BQL, {formData.full_name.split(" ")[0]}!
                  </h2>
                  <p className="text-gray-600">
                    Your professional account has been created successfully.
                  </p>
                </div>

                {/* Verification CTA */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <Fingerprint className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        Want to get verified?
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Verified pros get <span className="font-semibold text-blue-700">2x more leads</span> because customers prefer hiring trusted professionals.
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Quick ID verification (2-3 min)
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Verified badge on your profile
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Build trust with customers instantly
                    </div>
                  </div>

                  <IdentityVerificationCard 
                    onVerificationStart={() => {
                      toast.success("Verification started!");
                    }}
                  />
                </div>

                {/* Skip and Continue */}
                <div className="flex flex-col gap-3">
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={() => navigate("/pricing")}
                    data-testid="skip-verification-btn"
                  >
                    Skip for now - Continue to Subscription
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    You can verify your identity anytime from your dashboard
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Type Selection Screen
  if (!userType) {
    return (
      <div className="min-h-screen bg-sand">
        <Header />
        
        <div className="flex items-center justify-center min-h-screen pt-20 px-4">
          <div className="w-full max-w-2xl animate-fadeInUp">
            <div className="text-center mb-12">
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-gray-900 mb-4">
                Join BQL
              </h1>
              <p className="text-gray-600 text-lg">
                How would you like to use our platform?
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <button
                onClick={() => setUserType("customer")}
                className="card-base p-8 text-left hover-lift group"
                data-testid="select-customer-type"
              >
                <div className="w-16 h-16 rounded-2xl bg-forest/10 text-forest flex items-center justify-center mb-6 group-hover:bg-forest group-hover:text-white transition-colors">
                  <User className="w-8 h-8" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-gray-900 mb-2">
                  I Need Services
                </h3>
                <p className="text-gray-600 mb-4">
                  Post jobs for free and receive quotes from verified professionals.
                </p>
                <div className="flex items-center text-forest font-medium">
                  Get Started Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </button>

              <button
                onClick={() => setUserType("professional")}
                className="card-base p-8 text-left hover-lift group"
                data-testid="select-professional-type"
              >
                <div className="w-16 h-16 rounded-2xl bg-sienna/10 text-sienna flex items-center justify-center mb-6 group-hover:bg-sienna group-hover:text-white transition-colors">
                  <Wrench className="w-8 h-8" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-gray-900 mb-2">
                  I&apos;m a Professional
                </h3>
                <p className="text-gray-600 mb-4">
                  Access leads and grow your business with our subscription plans.
                </p>
                <div className="flex items-center text-sienna font-medium">
                  Start Earning
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 flex items-start gap-1">
                    <Shield className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
                    <span>Background check required within 30 days of subscribing</span>
                  </p>
                </div>
              </button>
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-600">
                Already have an account?{" "}
                <Link to="/login" className="text-forest font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Customer Registration Form
  if (userType === "customer") {
    return (
      <div className="min-h-screen bg-sand">
        <Header />
        
        <div className="flex items-center justify-center min-h-screen pt-20 px-4 pb-12">
          <div className="w-full max-w-md animate-fadeInUp">
            <button
              onClick={() => setUserType(null)}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>

            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-forest mb-4">
                <User className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading font-bold text-3xl text-gray-900 mb-2">
                Create Customer Account
              </h1>
              <p className="text-gray-600">
                Post jobs for free and get quotes
              </p>
            </div>

            <Card className="border-stone-200 shadow-lg">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="customer-register-form">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      placeholder="John Doe"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                      className="h-12"
                      data-testid="register-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="h-12"
                      data-testid="register-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        minLength={8}
                        className="h-12 pr-10"
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {/* Password Strength Indicator */}
                    {formData.password && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                          <Shield className="w-3 h-3" /> Password Requirements:
                        </p>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          {[
                            { key: 'length', label: '8+ characters' },
                            { key: 'uppercase', label: 'Uppercase letter' },
                            { key: 'lowercase', label: 'Lowercase letter' },
                            { key: 'number', label: 'Number' },
                            { key: 'special', label: 'Special character' },
                          ].map(({ key, label }) => {
                            const { checks } = validatePassword(formData.password);
                            return (
                              <span key={key} className={`flex items-center gap-1 ${checks[key] ? 'text-green-600' : 'text-gray-400'}`}>
                                {checks[key] ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone (Optional)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="h-12"
                      data-testid="register-phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Select value={formData.country} onValueChange={handleCountryChange}>
                      <SelectTrigger className="h-12" data-testid="register-country-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(country => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-forest hover:bg-forest-dark rounded-full text-base"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-gray-600 text-sm">
                    Already have an account?{" "}
                    <Link to="/login" className="text-forest font-medium hover:underline">
                      Sign in
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Professional Registration Form (Multi-step)
  return (
    <div className="min-h-screen bg-sand">
      <Header />
      
      <div className="flex items-center justify-center min-h-screen pt-20 px-4 pb-12">
        <div className="w-full max-w-lg animate-fadeInUp">
          <button
            onClick={() => step === 1 ? setUserType(null) : setStep(step - 1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-sienna mb-4">
              <Wrench className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading font-bold text-3xl text-gray-900 mb-2">
              Join as Professional
            </h1>
            <p className="text-gray-600">
              Step {step} of 2 - {step === 1 ? "Account Details" : "Business Info"}
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className={`step-indicator ${step >= 1 ? "active" : "pending"}`}>1</div>
            <div className="w-16 h-1 rounded bg-gray-200">
              <div className={`h-full rounded transition-all ${step >= 2 ? "w-full bg-forest" : "w-0"}`} />
            </div>
            <div className={`step-indicator ${step >= 2 ? "active" : "pending"}`}>2</div>
          </div>

          <Card className="border-stone-200 shadow-lg">
            <CardContent className="pt-6">
              <form onSubmit={step === 2 ? handleSubmit : (e) => { e.preventDefault(); setStep(2); }} className="space-y-4" data-testid="professional-register-form">
                
                {step === 1 && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="full_name">Full Name</Label>
                      <Input
                        id="full_name"
                        placeholder="John Doe"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        required
                        className="h-12"
                        data-testid="pro-register-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="h-12"
                        data-testid="pro-register-email-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                          minLength={8}
                          className="h-12 pr-10"
                          data-testid="pro-register-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      {/* Password Strength Indicator */}
                      {formData.password && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                            <Shield className="w-3 h-3" /> Password Requirements:
                          </p>
                          <div className="grid grid-cols-2 gap-1 text-xs">
                            {[
                              { key: 'length', label: '8+ characters' },
                              { key: 'uppercase', label: 'Uppercase letter' },
                              { key: 'lowercase', label: 'Lowercase letter' },
                              { key: 'number', label: 'Number' },
                              { key: 'special', label: 'Special character' },
                            ].map(({ key, label }) => {
                              const { checks } = validatePassword(formData.password);
                              return (
                                <span key={key} className={`flex items-center gap-1 ${checks[key] ? 'text-green-600' : 'text-gray-400'}`}>
                                  {checks[key] ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+1 (555) 123-4567"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="h-12"
                          data-testid="pro-register-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="country">Country</Label>
                        <Select value={formData.country} onValueChange={handleCountryChange}>
                          <SelectTrigger className="h-12" data-testid="pro-register-country-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map(country => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 bg-sienna hover:bg-sienna-dark rounded-full text-base"
                      data-testid="pro-register-next-btn"
                    >
                      Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </>
                )}

                {step === 2 && (
                  <>
                    {/* Background Check Notice */}
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4" data-testid="bg-check-notice">
                      <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-amber-800 font-medium text-sm">Background Check Required</p>
                          <p className="text-amber-700 text-xs mt-1">
                            All professionals must complete a background check within 30 days of subscribing to maintain their account and access leads.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="business_name">Business Name</Label>
                      <Input
                        id="business_name"
                        placeholder="Your Business Name"
                        value={formData.business_name}
                        onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                        required
                        className="h-12"
                        data-testid="pro-register-business-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="service_area">Service Area</Label>
                      <Input
                        id="service_area"
                        placeholder="e.g., Newark, NJ"
                        value={formData.service_area}
                        onChange={(e) => setFormData({ ...formData, service_area: e.target.value })}
                        className="h-12"
                        data-testid="pro-register-area-input"
                      />
                      <p className="text-xs text-gray-500">Enter your New Jersey service area to check for Founder&apos;s Rate eligibility</p>
                    </div>

                    {/* Pricing Info Banner */}
                    <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8a] rounded-xl p-4 text-white mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="w-5 h-5" />
                        <span className="font-semibold">Simple Pricing</span>
                      </div>
                      <ul className="text-sm space-y-1 text-white/90">
                        <li>• FREE to join — no signup fee</li>
                        <li>• Your first lead is free</li>
                        <li>• $29.99/month starts after your first lead — cancel anytime</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bio">About Your Services</Label>
                      <Textarea
                        id="bio"
                        placeholder="Tell customers about your experience and services..."
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        className="min-h-[100px]"
                        data-testid="pro-register-bio-input"
                      />
                    </div>

                    <div className="space-y-3">
                      <Label>Service Categories (Select all that apply)</Label>
                      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-1">
                        {categories.map(category => {
                          const isSelected = formData.selectedCategories.includes(category.id);
                          return (
                            <label
                              key={category.id}
                              className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                                isSelected
                                  ? "border-forest bg-forest/5"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                              data-testid={`category-checkbox-${category.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleCategoryToggle(category.id)}
                                className="w-4 h-4 rounded border-gray-300 text-forest focus:ring-forest"
                              />
                              <span className="text-sm">{category.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label>Accepted Payment Methods</Label>
                      <p className="text-sm text-gray-500">Select how customers can pay you</p>
                      <div className="grid grid-cols-2 gap-2">
                        {PAYMENT_METHODS.map(payment => {
                          const isSelected = formData.accepted_payments.includes(payment.id);
                          return (
                            <label
                              key={payment.id}
                              className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                                isSelected
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                              data-testid={`payment-checkbox-${payment.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handlePaymentToggle(payment.id)}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm">{payment.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Fee Disclosure */}
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="fee-disclosure">
                      <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        FREE to Join — No Signup Fee
                      </h4>
                      <ul className="space-y-2 text-sm text-green-800">
                        <li className="flex items-start gap-2">
                          <CreditCard className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                          <span>Your card will be saved on file (no charge now)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                          <span><strong>First lead is FREE</strong> — Then <strong>$29.99/month</strong> starts (cancel anytime)</span>
                        </li>
                      </ul>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 bg-sienna hover:bg-sienna-dark rounded-full text-base"
                      disabled={loading}
                      data-testid="pro-register-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Complete Registration — Free
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </>
                )}
              </form>

              <div className="mt-6 text-center">
                <p className="text-gray-600 text-sm">
                  Already have an account?{" "}
                  <Link to="/login" className="text-forest font-medium hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
